import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { Msp } from '../../../../../types';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';

interface BLPSelectorProps {
  onBlpBussSelect: (value: number) => void;
  onBlpMainBreakerRatingSelect: (value: number) => void;
  onBlpNewExistingChange: (value: NewExistingValue) => void;
  onBlpMainBreakerNewExistingChange: (value: NewExistingValue) => void;
  selectedBlpBuss?: number;
  selectedBlpMainBreakerRating?: number;
  blpNewExisting?: NewExistingValue;
  blpMainBreakerNewExisting?: NewExistingValue;
  blpBussOptions: Msp[];
  blpMainBreakerRatingOptions: Msp[];
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}

export const BLPSelector: React.FC<BLPSelectorProps> = ({
  onBlpBussSelect,
  onBlpMainBreakerRatingSelect,
  onBlpNewExistingChange,
  onBlpMainBreakerNewExistingChange,
  selectedBlpBuss,
  selectedBlpMainBreakerRating,
  blpNewExisting,
  blpMainBreakerNewExisting,
  blpBussOptions,
  blpMainBreakerRatingOptions,
  isLoading,
  error,
  isEnabled
}) => {

  const handleBlpBussSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onBlpBussSelect(0);
      return;
    }
    const blpBuss = blpBussOptions?.find(m => m.id === option.id);
    if (blpBuss) {
      onBlpBussSelect(parseInt(blpBuss.value, 10));
    }
  };

  const handleBlpMainBreakerRatingSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onBlpMainBreakerRatingSelect(0);
      return;
    }
    const blpMainBreakerRating = blpMainBreakerRatingOptions?.find(m => m.id === option.id);
    if (blpMainBreakerRating) {
      onBlpMainBreakerRatingSelect(parseInt(blpMainBreakerRating.value, 10));
    }
  };

  const blpBussDropdownOptions = blpBussOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const blpMainBreakerRatingDropdownOptions = blpMainBreakerRatingOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure BLP details</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* BLP Buss Rating */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
            BLP Buss Rating
          </label>
          {isEnabled && (
            <NewExistingSegment
              value={blpNewExisting || null}
              onChange={onBlpNewExistingChange}
              disabled={!isEnabled}
            />
          )}
        </div>
        <SearchDropdown
          options={blpBussDropdownOptions}
          onSelect={handleBlpBussSelect}
          placeholder="Choose BLP buss rating..."
          isLoading={isLoading}
          error={error}
          className="h-9"
          value={selectedBlpBuss ? { 
            id: blpBussOptions.find(opt => parseInt(opt.value, 10) === selectedBlpBuss)?.id || '', 
            label: selectedBlpBuss.toString() 
          } : null}
          disabled={!isEnabled}
        />
      </div>

      {/* BLP Main Breaker Rating */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
            BLP Main Breaker Rating
          </label>
          {isEnabled && (
            <NewExistingSegment
              value={blpMainBreakerNewExisting || null}
              onChange={onBlpMainBreakerNewExistingChange}
              disabled={!isEnabled}
            />
          )}
        </div>
        <SearchDropdown
          options={blpMainBreakerRatingDropdownOptions}
          onSelect={handleBlpMainBreakerRatingSelect}
          placeholder="Choose BLP main breaker rating..."
          isLoading={isLoading}
          error={error}
          className="h-9"
          value={selectedBlpMainBreakerRating ? { 
            id: blpMainBreakerRatingOptions.find(opt => parseInt(opt.value, 10) === selectedBlpMainBreakerRating)?.id || '', 
            label: selectedBlpMainBreakerRating.toString() 
          } : null}
          disabled={!isEnabled}
        />
      </div>
    </div>
  );
};